import videojs from 'video.js';

import './modal-dialog.css';

const _allModals = [];
const _displayAsPanel = new WeakMap();
const _showPlayerControls = new WeakMap();

export default class ModalDialog extends videojs.getComponent('ModalDialog') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} options
     * @param {string} options.text
     * @param {OVERLAY_POSITION} [options.position]
     * @param {string} [options.className]
     * @param {boolean} [options.displayAsPanel]
     * @param {boolean} [options.solidBackground]
     * @param {boolean} [options.showPlayerControls]
     */
    constructor(player, options) {
        super(player, options);
        this.addClass('mediasite-player__modal-dialog');
        this.displayAsPanel = options.displayAsPanel || false;
        this.solidBackground = options.solidBackground ?? true;
        _allModals.push(this);

        //the option to show the player controls needs to know about the player setting to show the player controls
        //therefore, we wait until the loader has finished before finding out if the modal dialog option of showing the player controls
        //should be applied or not
        player.on('presentationloaded', () => {            
            if (_allModals.indexOf(this) < 0) {
                return;
            }
            const playerHasControls = this.player().controls();
            if (playerHasControls && options.showPlayerControls) {
                this.showPlayerControls = true;
            } else {
                this.showPlayerControls = false;
            }
        });
    }

    open(...args) {
        super.open(...args);

        this.one('animationend', () => {
            const inputEl = this.el().querySelector('input')
            if (inputEl) {
                inputEl.focus();
            }
            else {
                //We need to focus on something in the modal dialog for accessibility reasons
                //If there is no input, we will focus on the modal dialog instead.
                this.el().focus();
            }
        });

        if (this.displayAsPanel) {
            const existingModal = _allModals.find(modal => modal.displayAsPanel && modal.opened() && modal !== this);
            existingModal?.close();
        }

        if (this.showPlayerControls) {
            this.player().controls(true);
        }

        this.triggerLayoutUpdate();

        this.trigger('modalopened');
    }

    close(...args) {
        if (super.opened()) {
            super.close(...args);
            const exitClass = 'mediasite-player__modal-dialog--exiting';
            try {
                this.addClass(exitClass);
                this.one('animationend', () => {
                    this.removeClass(exitClass);
                    this.triggerLayoutUpdate();
                });
            }
            catch (e) { }
        }
    }

    triggerLayoutUpdate() {
        if (_allModals.some(modal => modal.displayAsPanel && modal.opened())) {
            this.player().addClass('mediasite-player--sidebar-open');
            this.player().controls(true);
        }
        else {
            this.player().removeClass('mediasite-player--sidebar-open');
        }
        this.player().trigger('playerresize');
    }

    get displayAsPanel() {
        return _displayAsPanel.get(this);
    }

    set displayAsPanel(displayAsPanel) {
        _displayAsPanel.set(this, displayAsPanel);

        if (displayAsPanel) {
            this.addClass('mediasite-player__modal-dialog--panel');
        }
        else {
            this.removeClass('mediasite-player__modal-dialog--panel');
        }

        this.triggerLayoutUpdate();
    }

    get solidBackground() {
        return this.hasClass('mediasite-player__modal-dialog--solid-background');
    }

    set solidBackground(solidBackground) {
        if (solidBackground) {
            this.addClass('mediasite-player__modal-dialog--solid-background');
        }
        else {
            this.removeClass('mediasite-player__modal-dialog--solid-background');
        }
    }

    get showPlayerControls() {
        return _showPlayerControls.get(this);
    }

    set showPlayerControls(shouldShow) {
        _showPlayerControls.set(this, shouldShow);        

        if (shouldShow) {
            this.addClass('mediasite-player__modal-dialog---show-controls');
        }
        else {
            this.removeClass('mediasite-player__modal-dialog---show-controls');
        }

        this.triggerLayoutUpdate();
    }

    /**
     * See the 'condtionalBlur' comment for more details, but this function moves the focus off the modal when it opens back to where the focus was before
     * if the controls are enabled to be shown.
     */
    conditionalFocus_() {
        super.conditionalFocus_();

        if (this.showPlayerControls && this.previouslyActiveEl_) {
            this.previouslyActiveEl_.focus();
        }
    }

    /**
     * This is how the official Video.js "TextTrackSettings" component resets focus to the captions button on close.
     * I don't like how it's labeled in the Video.js source code as a private function, but it appears to be the
     * only way to do this accessibly without reimplementing more of the modal component.
     * @see {@link https://github.com/videojs/video.js/blob/main/src/js/tracks/text-track-settings.js#L589}
     * @private
     */
    conditionalBlur_() {
        if (this.previouslyActiveEl_ && this.previouslyActiveEl_.classList.contains('vjs-menu-item')) {
            // ModalDialog component automatically focuses this.previouslyActiveEl_ on close; setting it here lets us override which button gets focused
            this.previouslyActiveEl_ = this.previouslyActiveEl_.closest('.vjs-menu-button').querySelector('.vjs-menu-button');
        }
        super.conditionalBlur_();
    }

    dispose() {
        super.dispose();
        const indexInArray = _allModals.indexOf(this);
        if (indexInArray > -1) {
            _allModals.splice(indexInArray, 1);
        }
    }
}
